import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { Button, Flex, Text } from '@nula/core-ui';
import type { UIMessage } from 'ai';
import styles from './user-message.module.css';

const root = cva(styles.root);

interface UserMessageProps {
  /** A Vercel SDK v6 UIMessage with role 'user'. */
  message: UIMessage;
  className?: string;
}

/** Concatenate all text parts in order. */
function userText(message: UIMessage): string {
  let acc = '';
  for (const part of message.parts ?? []) {
    if (part.type === 'text') acc += (part as { type: 'text'; text: string }).text;
  }
  return acc;
}

export function UserMessage({ message, className }: UserMessageProps) {
  const bubbleRef = useRef<HTMLElement>(null);

  // When collapsed, CSS line-clamps the bubble at 6 lines. We measure
  // scrollHeight vs clientHeight to know whether the content actually
  // overflows that clamp — only then do we render the Show more affordance.
  // Pure-CSS line-clamp can't reveal "is overflowing"; the JS measurement
  // is the only reliable signal.
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const measure = useCallback(() => {
    const el = bubbleRef.current;
    if (!el) return;
    // When expanded the clamp is off — clientHeight == scrollHeight tautologically.
    // Skip the measurement so we don't toggle overflowing off and lose the button.
    if (expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  // Measure on mount + whenever the message text changes.
  useLayoutEffect(() => {
    measure();
  }, [measure, message]);

  // Remeasure on resize (window or container width change re-wraps lines).
  useEffect(() => {
    const el = bubbleRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const collapsed = overflowing && !expanded;
  const text = userText(message);

  const handleBubbleClick = useCallback(() => {
    // Click-anywhere-on-bubble expands. Only meaningful when collapsed —
    // clicking the expanded bubble does nothing (avoid surprise collapse).
    if (collapsed) setExpanded(true);
  }, [collapsed]);

  return (
    <motion.div
      className={clsx(root(), className)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <Flex
        ref={bubbleRef}
        className={styles.bubble}
        data-collapsed={collapsed ? 'true' : 'false'}
        data-expanded={expanded ? 'true' : 'false'}
        onClick={handleBubbleClick}
        role={collapsed ? 'button' : undefined}
        tabIndex={collapsed ? 0 : undefined}
        aria-expanded={collapsed ? false : undefined}
      >
        <Text className={styles.text}>{text}</Text>
      </Flex>
      {overflowing && (
        <Button
          variant="text"
          size="sm"
          className={styles.toggle}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </motion.div>
  );
}
